
import { EmptyInstance } from "core/features/components/mixins/empty-instance";
import { EventEmitterMixin } from "front-end/mixins/event-emitter.mixin";
import { FilterDropdownParameterComponent } from "./components/filter-parameters/filter-dropdown-parameter.component";
import { FilterMultichoiceListParameterComponent } from "./components/filter-parameters/filter-multichoice-list.parameter.component";
import { IFilterParameterComponent } from "./components/filter-parameters/filter-parameter.component";
import { FilterParameterModel } from "./components/filter-parameters/models/filter-parameter.model";
import { ParametersPanelComponent } from "./components/parameters-panel/parameters-panel.component";
import { SearchFilterModel } from "./models/search-filter.model";

export class NewsFilterComponent extends EventEmitterMixin(EmptyInstance) {
    public static SELECTOR: string = ".js-ina-filter";

    private _currentContext: JQuery = $();

    private _classMap = {
        css: {
            show: "show"
        },
        js: {
            toggle: `${NewsFilterComponent.SELECTOR}__toggle`,
            content: `${NewsFilterComponent.SELECTOR}__content`,
            clearBtn: `${NewsFilterComponent.SELECTOR}__clear-btn`,
            cancelBtn: `${NewsFilterComponent.SELECTOR}__cancel-btn`,
            submitBtn: `${NewsFilterComponent.SELECTOR}__submit-btn`
        }
    };

    private _toggle: JQuery;
    private _content: JQuery;
    private _clearBtn: JQuery;
    private _cancelBtn: JQuery;
    private _submitBtn: JQuery;

    protected _params: { [name: string]: IFilterParameterComponent } = {};
    private _paramsPanel: ParametersPanelComponent = null;

    private _isVisible: boolean = false;

    private setInitialState(): void {
        this.checkClearBtnVisibility();
        Object.keys(this._params).forEach(key => this.displayParamsOnPanel(this._params[key].getParam()));
    }

    protected toggleFilter(): void {
        this._isVisible = !this._isVisible;
        this._currentContext.toggleClass(this._classMap.css.show);
        this._content.toggleClass(this._classMap.css.show);
        this._toggle.attr("aria-expanded", String(this._isVisible));
    }

    private toggleClearBtn(state: boolean): void {
        this._clearBtn.toggle(state);
    }

    private checkClearBtnVisibility(): void {
        const isEmptyFilter = Object.keys(this._params).every(key => this._params[key].isEmpty());
        this.toggleClearBtn(!isEmptyFilter);
    }

    private displayParamsOnPanel(data: FilterParameterModel): void {
        this._paramsPanel.syncParameters(data);
    }

    private handleClearBtnClick(): void {
        if (this._isVisible) {
            Object.keys(this._params).forEach(key => this._params[key].reset());
            this.toggleClearBtn(false);
        } else {
            const filter: SearchFilterModel = {};
            Object.keys(this._params).forEach(key => filter[key] = "");
            this.emit("submit", filter);
        }
    }

    private handleCancelButtonClick(): void {
        const filter: SearchFilterModel = Object.fromEntries(new URLSearchParams(location.search));
        Object.keys(this._params).forEach(key => this._params[key].reset(String(filter[key] ?? "")));
        this.toggleFilter();
    }

    private handleSubmitButtonClick(): void {
        const filter: SearchFilterModel = {};
        Object.keys(this._params).forEach(key => filter[key] = this._params[key].getValue());
        this.emit("submit", filter);
    }

    private handleFilterChanges(data: FilterParameterModel): void {
        this.checkClearBtnVisibility();
        this.displayParamsOnPanel(data);
    }

    private handleParamValueRemoval(): void {
        if (!this._isVisible) {
            const filter: SearchFilterModel = {};
            Object.keys(this._params).forEach(key => filter[key] = this._params[key].getValue());
            this.emit("submit", filter);
        }
    }

    private bindEvents(): void {
        this._toggle.on("click", () => this.toggleFilter());
        this._clearBtn.on("click", () => this.handleClearBtnClick());
        this._cancelBtn.on("click", () => this.handleCancelButtonClick());
        this._submitBtn.on("click", () => this.handleSubmitButtonClick());

        Object.keys(this._params)
            .forEach(key => this._params[key]
                .on("changed")
                .subscribe((data: FilterParameterModel) => this.handleFilterChanges(data)));

        this._paramsPanel.on("params/remove").subscribe((param: {group: string, value: string}) => {
            this._params[param.group].removeValue(param.value);
            this.handleParamValueRemoval();
        });
    }

    private parseContext(): void {
        this._toggle = $(this._classMap.js.toggle, this._currentContext);
        this._content = $(this._classMap.js.content, this._currentContext);
        this._clearBtn = $(this._classMap.js.clearBtn, this._currentContext);
        this._cancelBtn = $(this._classMap.js.cancelBtn, this._currentContext);
        this._submitBtn = $(this._classMap.js.submitBtn, this._currentContext);

        const paramsPanel = $(ParametersPanelComponent.SELECTOR, this._currentContext);
        this._paramsPanel = new ParametersPanelComponent();
        this._paramsPanel.init(paramsPanel);

        const multiChoices = $(FilterMultichoiceListParameterComponent.SELECTOR, this._currentContext);
        multiChoices.each((index, entry) => {
            const multiChoiceContext = $(entry);
            const multiChoice = new FilterMultichoiceListParameterComponent();
            multiChoice.init(multiChoiceContext);
            this._params[multiChoice.getName()] = multiChoice;
        });

        const dropdowns = $(FilterDropdownParameterComponent.SELECTOR, this._currentContext);
        dropdowns.each((index, entry) => {
            const $dropdownContext = $(entry);
            const dropdown = new FilterDropdownParameterComponent();
            dropdown.init($dropdownContext);
            this._params[dropdown.getName()] = dropdown;
        });
    }

    public init(context: JQuery): void {
        this._currentContext = context || this._currentContext;

        this.parseContext();
        this.bindEvents();

        Object.keys(this._params)
            .forEach(key => this._params[key].setInitialValue());

        this.setInitialState();
    }
}
